/**
 * AutomationRuntimeBridge
 *
 * Exposes automation execution runtime state (run history, live run) from the
 * React service layer (LoggingService / ExecutionService) to Lit canvas
 * blocks, following the `__databaseBridge` pattern
 * (see use-database-discovery-bridge.ts).
 *
 * Local-first contract: canvas blocks render persisted Y.js props first and
 * treat this bridge as an additive overlay. When the bridge is absent or the
 * backend is offline, the last known in-memory state stays in place — no
 * loading spinners, no layout shift.
 */

import {
  canAttemptBackendRequest,
  onBackendAvailable,
} from '../../../utils/backend-availability';
import type {
  AutomationConfigApiService,
  ScheduleResponse,
} from './automation-config-api';
import type { ExecutionService, ExecutionState } from './execution';
import type { ExecutionLog, LoggingService } from './logging';

// =============================================================================
// Public snapshot types (consumed by the Lit canvas block)
// =============================================================================

export interface AutomationRunSummary {
  id: string;
  status: 'success' | 'error' | 'cancelled' | 'running';
  startedAt: number;
  completedAt?: number;
  durationMs?: number;
  error?: string;
  /** Number of workflow node steps in this run (from process stage) */
  stepCount?: number;
  /** Rows passed into the workflow during this run. */
  inputRows?: number;
  rowsWritten?: number;
}

export interface AutomationActiveRun {
  startedAt: number;
  stepName?: string;
  /** 1-based index of the currently running workflow step */
  stepIndex?: number;
  stepCount?: number;
  /** 0-100 overall progress if known */
  percent?: number;
}

export interface AutomationRuntimeSnapshot {
  /** Completed runs, newest first (capped by LoggingService history size) */
  history: AutomationRunSummary[];
  /** Currently running execution, if any */
  activeRun: AutomationActiveRun | null;
  /** Backend-owned schedule state. Never derive this from the browser clock. */
  schedule: AutomationScheduleSnapshot | null;
}

export interface AutomationScheduleSnapshot {
  enabled: boolean;
  lastRunAt: number | null;
  nextRunAt: number | null;
}

export interface AutomationRuntimeUpdateDetail {
  blockId: string;
}

declare global {
  interface Window {
    /** Runtime state bridge for Lit automation canvas blocks */
    __automationRuntimeBridge?: {
      getRuntime: (blockId: string) => AutomationRuntimeSnapshot | null;
      /**
       * Attach live subscriptions for a block and lazily fetch its
       * persisted history from the backend (once per session, deferred
       * while the backend is unavailable).
       */
      ensureRuntime: (blockId: string, docId?: string) => void;
      releaseRuntime: (blockId: string) => void;
      refreshSchedule: (blockId: string, docId: string) => void;
      setSchedule: (blockId: string, schedule: ScheduleResponse | null) => void;
    };
  }
  interface WindowEventMap {
    'automation-runtime-updated': CustomEvent<AutomationRuntimeUpdateDetail>;
    'automation-runtime-ready': CustomEvent<void>;
  }
}

// =============================================================================
// Module state
// =============================================================================

interface RuntimeSource {
  configApi: AutomationConfigApiService;
  logging: LoggingService;
  execution: ExecutionService;
  getWorkspaceId: () => string;
}

let source: RuntimeSource | null = null;

const blockSubscriptions = new Map<
  string,
  Array<{ unsubscribe: () => void }>
>();
const historyRequested = new Set<string>();
const pendingHistory = new Set<string>();
const schedules = new Map<string, AutomationScheduleSnapshot | null>();
const scheduleDocIds = new Map<string, string>();
const scheduleRequests = new Map<string, Promise<void>>();
let availabilityUnsubscribe: (() => void) | null = null;

// =============================================================================
// Mapping
// =============================================================================

function mapLogStatus(
  status: ExecutionLog['status']
): AutomationRunSummary['status'] {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'cancelled':
      return 'cancelled';
    case 'running':
    case 'awaiting_external':
      return 'running';
  }
}

function mapLog(log: ExecutionLog): AutomationRunSummary {
  const durationMs =
    log.completedAt !== undefined
      ? Math.max(0, log.completedAt - log.startedAt)
      : undefined;
  return {
    id: log.id,
    status: mapLogStatus(log.status),
    startedAt: log.startedAt,
    completedAt: log.completedAt,
    durationMs,
    error: log.error ?? log.process?.error,
    stepCount: log.process?.nodeSteps?.length,
    inputRows: log.input?.passedFilter,
    rowsWritten: log.output?.rowsWritten,
  };
}

function buildActiveRun(
  activeLog: ExecutionLog | null,
  state: ExecutionState
): AutomationActiveRun | null {
  if (!activeLog) return null;

  const steps = activeLog.process?.nodeSteps;
  const runningIndex = steps?.findIndex(step => step.status === 'running');
  const hasRunningStep = runningIndex !== undefined && runningIndex >= 0;

  return {
    startedAt: activeLog.startedAt,
    stepName:
      state.progress.media?.stepName ??
      (hasRunningStep ? steps?.[runningIndex]?.name : undefined),
    stepIndex: hasRunningStep ? runningIndex + 1 : undefined,
    stepCount: steps?.length,
    percent:
      state.status === 'running' || state.status === 'queued'
        ? state.progress.percentage
        : undefined,
  };
}

// =============================================================================
// Subscriptions & history fetch
// =============================================================================

function dispatchUpdate(blockId: string): void {
  window.dispatchEvent(
    new CustomEvent<AutomationRuntimeUpdateDetail>(
      'automation-runtime-updated',
      { detail: { blockId } }
    )
  );
}

export function toAutomationScheduleSnapshot(
  schedule: ScheduleResponse | null
): AutomationScheduleSnapshot | null {
  if (!schedule) return null;
  const lastRunAt = schedule.lastRunAt ? Date.parse(schedule.lastRunAt) : NaN;
  const nextRunAt = schedule.nextRunAt ? Date.parse(schedule.nextRunAt) : NaN;
  return {
    enabled: schedule.enabled,
    lastRunAt: Number.isFinite(lastRunAt) ? lastRunAt : null,
    nextRunAt: Number.isFinite(nextRunAt) ? nextRunAt : null,
  };
}

export function resolveRefreshedAutomationSchedule(
  current: AutomationScheduleSnapshot | null | undefined,
  schedule: ScheduleResponse | null | undefined
): AutomationScheduleSnapshot | null {
  const refreshed = toAutomationScheduleSnapshot(schedule ?? null);
  // A config refresh can briefly observe the config before its joined schedule
  // row is visible. Keep the last backend-owned projection through that
  // transient boundary; explicit enable/disable writes still use setSchedule
  // below and therefore update immediately.
  return refreshed ?? current ?? null;
}

export function setAutomationSchedule(
  blockId: string,
  schedule: ScheduleResponse | null
): void {
  schedules.set(blockId, toAutomationScheduleSnapshot(schedule));
  dispatchUpdate(blockId);
}

export function refreshAutomationSchedule(
  blockId: string,
  docId: string
): void {
  if (!source || scheduleRequests.has(blockId)) return;
  scheduleDocIds.set(blockId, docId);
  const request = source.configApi
    .getConfig(blockId, source.getWorkspaceId(), docId)
    .then(config => {
      schedules.set(
        blockId,
        resolveRefreshedAutomationSchedule(
          schedules.get(blockId),
          config?.schedule
        )
      );
      dispatchUpdate(blockId);
    })
    .finally(() => {
      scheduleRequests.delete(blockId);
    });
  scheduleRequests.set(blockId, request);
}

function attachBlockSubscriptions(blockId: string): void {
  if (!source || blockSubscriptions.has(blockId)) return;

  const notify = () => {
    dispatchUpdate(blockId);
    const docId = scheduleDocIds.get(blockId);
    if (docId) refreshAutomationSchedule(blockId, docId);
  };
  blockSubscriptions.set(blockId, [
    source.logging.getLogs$(blockId).subscribe(notify),
    source.logging.getActiveLog$(blockId).subscribe(notify),
    source.execution.getState$(blockId).subscribe(notify),
  ]);
}

function requestHistory(blockId: string): void {
  if (!source || historyRequested.has(blockId)) return;

  if (!canAttemptBackendRequest()) {
    // Offline: defer without marking as done — flushed on availability.
    pendingHistory.add(blockId);
    return;
  }

  historyRequested.add(blockId);
  pendingHistory.delete(blockId);
  source.logging.refreshLogs(blockId, source.getWorkspaceId());
}

function flushPendingHistory(): void {
  if (!canAttemptBackendRequest()) return;
  for (const blockId of Array.from(pendingHistory)) {
    requestHistory(blockId);
  }
}

// =============================================================================
// Bridge lifecycle (called by useAutomationRuntimeBridge)
// =============================================================================

export function getAutomationRuntime(
  blockId: string
): AutomationRuntimeSnapshot | null {
  if (!source) return null;

  const logs = source.logging.getLogs$(blockId).value;
  const activeLog =
    source.logging.getActiveLog$(blockId).value ??
    logs.find(
      log => log.status === 'running' || log.status === 'awaiting_external'
    ) ??
    null;
  const state = source.execution.getState$(blockId).value;

  return {
    history: logs.map(mapLog),
    activeRun: buildActiveRun(activeLog, state),
    schedule: schedules.get(blockId) ?? null,
  };
}

export function ensureAutomationRuntime(blockId: string, docId?: string): void {
  if (!source) return;
  attachBlockSubscriptions(blockId);
  requestHistory(blockId);
  if (docId) refreshAutomationSchedule(blockId, docId);
}

export function releaseAutomationRuntime(blockId: string): void {
  const subscriptions = blockSubscriptions.get(blockId);
  if (subscriptions) {
    for (const subscription of subscriptions) {
      subscription.unsubscribe();
    }
    blockSubscriptions.delete(blockId);
  }
  pendingHistory.delete(blockId);
  schedules.delete(blockId);
  scheduleDocIds.delete(blockId);
}

export function setAutomationRuntimeSource(
  configApi: AutomationConfigApiService,
  logging: LoggingService,
  execution: ExecutionService,
  getWorkspaceId: () => string
): void {
  clearAutomationRuntimeSource();
  source = { configApi, logging, execution, getWorkspaceId };

  availabilityUnsubscribe = onBackendAvailable(() => {
    flushPendingHistory();
  });

  window.__automationRuntimeBridge = {
    getRuntime: getAutomationRuntime,
    ensureRuntime: ensureAutomationRuntime,
    releaseRuntime: releaseAutomationRuntime,
    refreshSchedule: refreshAutomationSchedule,
    setSchedule: setAutomationSchedule,
  };
  window.dispatchEvent(new CustomEvent('automation-runtime-ready'));
}

export function clearAutomationRuntimeSource(): void {
  for (const subscriptions of blockSubscriptions.values()) {
    for (const subscription of subscriptions) {
      subscription.unsubscribe();
    }
  }
  blockSubscriptions.clear();
  historyRequested.clear();
  pendingHistory.clear();
  schedules.clear();
  scheduleDocIds.clear();
  scheduleRequests.clear();
  availabilityUnsubscribe?.();
  availabilityUnsubscribe = null;
  source = null;
  delete window.__automationRuntimeBridge;
}
